<template>
  <div>
    <loading-panel :loading="loading"></loading-panel>

    <!--    Breadcrumbs-->
    <breadcrumb style="margin-left: -15px">
      <breadcrumb-item>
        <a href="/roadmap">Roadmap</a>
      </breadcrumb-item>
      <breadcrumb-item>
        <span>{{ ticket.ticket_key }}</span>
      </breadcrumb-item>
    </breadcrumb>

    <div class="row">
      <!--  MAIN CONTENT  -->
      <div class="col-lg-8">
        <ticket-card :ticket-key="ticket.ticket_key" :ticket-title="ticket.title">
          <template #title-suffix>
            <i v-if="ticket.watching" class="tim-icons el-icon-view text-info ml-2"></i>
          </template>

          <!--  DESCRIPTION  -->
          <ticket-description :value="ticket.description || ''" :dark="isDarkMode" />

          <!--  COMMENTS  -->
          <ticket-comments
            ref="ticketComments"
            title="Comments"
            show-company
            :comments="comments"
            :can-comment="hasPermissions('roadmap_comment')"
            :can-edit="canEditComment"
            :saving="savingComment"
            :deleting-id="deletingCommentId"
            :dark="isDarkMode"
            @create="createComment"
            @update="updateComment"
            @delete="deleteComment"
            @checklist-change="handleCommentCheckbox"
          />
        </ticket-card>
      </div>

      <!--  SIDEBAR  -->
      <div class="col-lg-4">
        <!--  VOTE + WATCH actions above the card  -->
        <div class="d-flex align-items-center flex-wrap mb-3">
          <el-dropdown v-if="canAddVote || canRemoveVote" trigger="click" class="mr-2">
            <base-button size="sm" :type="ticket.my_vote_count > 0 ? 'primary' : 'default'">
              <i class="fa fa-thumbs-up mr-1"></i>
              <span class="ml-1">{{ ticket.vote_count || 0 }}</span>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a v-if="canAddVote" class="dropdown-item mt-2 mb-2" href="#" @click.prevent="modifyTicket('vote', 1)">
                  <i style="color: #42b883" class="fa fa-thumbs-up mr-2"></i> Vote for this ticket
                </a>
                <a v-if="canRemoveVote" class="dropdown-item mt-2 mb-2" href="#" @click.prevent="modifyTicket('vote', 0)">
                  <i class="fa fa-thumbs-down mr-2 text-danger"></i> Remove vote
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-dropdown v-if="hasPermissions('roadmap_watch')" trigger="click">
            <base-button size="sm" :type="ticket.watching ? 'primary' : 'default'">
              <i class="tim-icons el-icon-view mr-1"></i>
              <span class="ml-1">{{ ticket.watcher_count || 0 }}</span>
            </base-button>
            <template #dropdown>
              <el-dropdown-menu>
                <a v-if="!ticket.watching" class="dropdown-item mt-2 mb-2" href="#" @click.prevent="modifyTicket('watch', 1)">
                  <i class="tim-icons el-icon-view text-default mr-2"></i> Watch ticket
                </a>
                <a v-else class="dropdown-item mt-2 mb-2" href="#" @click.prevent="modifyTicket('watch', 0)">
                  <i class="tim-icons el-icon-view text-danger mr-2"></i> Stop watching ticket
                </a>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <ticket-card header="Details" compact>
          <ticket-detail-field label="Status">
            <badge v-if="ticket.status_name" :style="{'background-color': ticket.status_label_color}">{{ ticket.status_name }}</badge>
          </ticket-detail-field>

          <ticket-detail-field label="Ticket type">
            <span class="detail-text-value">{{ ticket.ticket_type_name }}</span>
          </ticket-detail-field>

          <ticket-detail-field label="Feature">
            <badge v-if="ticket.feature_name" :style="{'background-color': ticket.feature_badge_color}">{{ ticket.feature_name }}</badge>
          </ticket-detail-field>

          <ticket-detail-field label="Priority">
            <template v-if="ticket.priority">
              <i :class="ticket.priority.icon" class="detail-icon" :style="{ color: ticket.priority.color }" aria-hidden="true"></i>
              <span class="detail-text-value">{{ ticket.priority.name }}</span>
            </template>
          </ticket-detail-field>

          <ticket-detail-field label="Votes">
            <span class="detail-text-value">{{ ticket.vote_count || 0 }}</span>
            <span v-if="ticket.my_vote_count > 0" class="detail-text-value text-muted">({{ ticket.my_vote_count }} yours)</span>
          </ticket-detail-field>

          <ticket-detail-field label="Watchers">
            <span class="detail-text-value">{{ ticket.watcher_count || 0 }}</span>
          </ticket-detail-field>

          <ticket-detail-field label="Last updated">
            <span class="detail-text-value">{{ getLocalDateTime(ticket.last_updated || ticket.time_created) }}</span>
          </ticket-detail-field>

          <ticket-detail-field v-if="ticket.planned_at" label="Planned since">
            <span class="detail-text-value">{{ getLocalDateTime(ticket.planned_at) }}</span>
          </ticket-detail-field>
        </ticket-card>
      </div>
    </div>

    <base-alert-dialog
      :show="alert.show"
      :title="alert.title"
      :message="alert.message"
      :variant="alert.variant"
      @confirm="alert.show = false"
      @cancel="alert.show = false"
    />
  </div>
</template>

<script>
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";
import {Auth} from "aws-amplify";
import {Badge} from "@/components";
import BreadcrumbItem from "@/components/Breadcrumb/BreadcrumbItem";
import Breadcrumb from "@/components/Breadcrumb/Breadcrumb";
import {Dropdown, DropdownMenu} from "element-ui";
import TicketCard from "@/components/Tickets/TicketCard";
import TicketDetailField from "@/components/Tickets/TicketDetailField";
import TicketDescription from "@/components/Tickets/TicketDescription";
import TicketComments from "@/components/Tickets/TicketComments";

// Statuses where voting is closed.
const VOTING_CLOSED_STATUSES = [2, 4, 5, 9, 10]

export default {
  name: "TicketDetails",
  components: {
    Badge,
    BreadcrumbItem,
    Breadcrumb,
    TicketCard,
    TicketDetailField,
    TicketDescription,
    TicketComments,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
  },
  data(){
    return {
      loading: false,
      priorities: [
        {id: 1, name: "Highest", icon: 'fa fa-angle-double-up', color: '#ec250d'},
        {id: 2, name: "High", icon: 'fa fa-angle-up', color: '#ec250d'},
        {id: 3, name: "Medium", icon: 'tim-icons icon-simple-delete', color: '#ff8d72'},
        {id: 4, name: "Low", icon: 'fa fa-angle-down', color: '#1d8cf8'},
        {id: 5, name: "Lowest", icon: 'fa fa-angle-double-down', color: '#1d8cf8'},
      ],
      ticket: {},
      comments: [],
      total_vote_count: 0,
      available_votes: 5,
      savingComment: false,
      deletingCommentId: null,
      alert: { show: false, title: '', message: '', variant: 'success' }
    }
  },
  computed: {
    isDarkMode() {
      return !document.body.classList.contains('white-content')
    },
    votingOpen() {
      return !VOTING_CLOSED_STATUSES.includes(this.ticket.status)
    },
    canAddVote() {
      return this.hasPermissions('roadmap_vote') && this.votingOpen && this.total_vote_count < this.available_votes
    },
    canRemoveVote() {
      return this.hasPermissions('roadmap_vote') && this.votingOpen && this.ticket.my_vote_count > 0
    }
  },
  methods: {
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions')) || []
      return permissions.some(element => element === scope)
    },
    canEditComment(c){
      let loggedInUser = Number(JSON.parse(localStorage.getItem('profile')).id)
      return c.user_id === loggedInUser
    },
    showAlert(title, message, variant = 'success'){
      this.alert = { show: true, title, message, variant }
    },
    ticketUrl(suffix = ''){
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let ticketId = this.$route.query.id
      return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/work/' + ticketId + suffix
    },
    handleError(err){
      console.log(err)
      if(err.response && err.response.status == 401){
        this.$router.push('/forbidden')
      }
    },
    async modifyTicket(action, modifier){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      axios.post(this.ticketUrl(), { action, modifier }, config)
        .then(response => {
          this.loading = false
          let text = 'You are now watching this ticket'
          if(action === 'vote' && modifier === 1){
            text = 'You added a vote to the ticket'
          } else if(action === 'vote' && modifier === 0){
            text = 'You removed a vote from the ticket'
          } else if(action === 'watch' && modifier === 0){
            text = 'You stopped watching this ticket'
          }
          this.showAlert('Success', text, 'success')
          this.getTicket()
        })
        .catch(err => {
          this.loading = false
          this.handleError(err)
          if(err.response && err.response.status == 500){
            const body = err.response.data
            this.showAlert('Error', typeof body === 'string' ? body : 'The ticket could not be updated.', 'danger')
          }
        })
    },
    async deleteComment(item){
      this.deletingCommentId = item.id
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      axios.delete(this.ticketUrl('/comments/' + item.id), config)
        .then(response => {
          this.deletingCommentId = null
          this.getTicket()
        })
        .catch(err => {
          this.deletingCommentId = null
          this.handleError(err)
        })
    },
    async updateComment({ comment, html }){
      this.savingComment = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      axios.put(this.ticketUrl('/comments/' + comment.id), { comment: html }, config)
        .then(response => {
          this.savingComment = false
          if (this.$refs.ticketComments) this.$refs.ticketComments.reset()
          this.getTicket()
        })
        .catch(err => {
          this.savingComment = false
          this.handleError(err)
        })
    },
    async createComment(html){
      this.savingComment = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      axios.post(this.ticketUrl('/comments'), { comment: html }, config)
        .then(response => {
          this.savingComment = false
          if (this.$refs.ticketComments) this.$refs.ticketComments.reset()
          this.getTicket()
        })
        .catch(err => {
          this.savingComment = false
          this.handleError(err)
        })
    },
    async handleCommentCheckbox({ comment, html, revert }){
      try {
        let token = await this.getAuthToken()
        let config = { headers: { Authorization: token } }
        await axios.put(this.ticketUrl('/comments/' + comment.id), { comment: html }, config)
        this.$set(comment, 'comment', html)
      } catch (err) {
        console.log(err)
        revert()
        this.$notify({ type: 'danger', message: 'Failed to update checklist' })
      }
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async loadAllComments() {
      const loadPromises = this.comments
        .filter(c => c.has_content)
        .map(comment => this.loadCommentContent(comment));

      await Promise.all(loadPromises);
    },
    async loadCommentContent(comment) {
      if (!comment.has_content) return;

      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const response = await axios.get(this.ticketUrl('/comments/' + comment.id), config);
        this.$set(comment, 'comment', response.data.content);
      } catch (error) {
        console.error(`Error loading comment ${comment.id}:`, error);
        this.$set(comment, 'comment', '<p style="color: red;">Error loading comment</p>');
      }
    },
    async getTicket(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }

      axios.get(this.ticketUrl(), config)
        .then(response => {
          this.loading = false
          this.ticket = response.data.ticket
          this.comments = response.data.comments
          this.total_vote_count = response.data.total_vote_count
          this.ticket.priority = this.getPriority(this.ticket.priority)
          this.loadAllComments();
        })
        .catch(err => {
          this.loading = false
          this.handleError(err)
        })
    },
    getLocalDateTime(d){
      if(!d) return ''
      const date = formatGroupDate(d, { dayOfWeek: false })
      const time = formatGroupTime(d)
      if(!date && !time) return ''
      return `${date}, ${time}`
    },
    getPriority(priority){
      return this.priorities.find(p => p.id === priority) || null
    },
  },
  mounted(){
    this.getTicket()
  }
}
</script>
